import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiService } from '@/services/api/ApiService';
import { usePrintOrder } from './usePrintOrder';
import { usePlacementChecklist } from './useActivationLocalState';
import {
  ActivationProgress,
  ActivationStepState,
  PLACEMENT_SPOTS,
} from '@/types/activation/activation-local';
import type { GetActivitiesResponse } from '@/types/activity';

const SCAN_ACTION = 'qr_scanned';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/**
 * Start of the trailing 7-day window, truncated to the hour so the query key
 * is stable and the count doesn't refetch on every render.
 */
const weekWindowStart = (): string =>
  new Date(Math.floor((Date.now() - WEEK_MS) / HOUR_MS) * HOUR_MS).toISOString();

/**
 * Counts QR scans for a Genie. Same activity source the scan activity list
 * reads (`action: 'qr_scanned'`, `resourceId: <agentId>`); we only need the
 * total, so this asks for one row and uses the response count.
 */
const useScanCount = (agentId: string | null | undefined, startDate?: string) =>
  useQuery({
    queryKey: ['activation-scan-count', agentId, startDate ?? 'all'],
    queryFn: () =>
      ApiService.invoke<GetActivitiesResponse>({
        resource: 'activities',
        action: 'all',
        data: {
          action: SCAN_ACTION,
          resourceId: agentId,
          limit: 1,
          offset: 0,
          ...(startDate ? { startDate } : {}),
        },
      }),
    enabled: !!agentId,
    staleTime: 2 * 60 * 1000,
  });

/**
 * PRINTED / PLACED / SCANNED for one activation pack.
 *
 * PRINTED and SCANNED come from real data (the print order's own status, and
 * QR scan activity). PLACED is the client-side lookbook checklist — no backend
 * integration by design.
 *
 * `printedButNeverScanned` is the honest nudge this exposes for the dashboard's
 * next-action line: artwork went to a printer, and nobody has ever scanned it.
 */
export const useActivationProgress = (
  packId: string | undefined,
  agentId: string | null | undefined,
): ActivationProgress => {
  const { data: order, isLoading: isLoadingOrder } = usePrintOrder(packId || '');
  const { checkedCount } = usePlacementChecklist(packId);

  const windowStart = useMemo(weekWindowStart, []);
  const { data: totalScans, isLoading: isLoadingTotal } = useScanCount(agentId);
  const { data: weekScans, isLoading: isLoadingWeek } = useScanCount(agentId, windowStart);

  return useMemo(() => {
    const status = order?.status;
    const printed: ActivationStepState =
      status === 'sent' || status === 'fulfilled'
        ? 'done'
        : status === 'sending'
          ? 'in_progress'
          : 'todo';

    const placedTotal = PLACEMENT_SPOTS.length;
    const placed: ActivationStepState =
      checkedCount === 0 ? 'todo' : checkedCount >= placedTotal ? 'done' : 'in_progress';

    const scansTotal = totalScans?.count ?? 0;
    const scansThisWeek = weekScans?.count ?? 0;
    const scanned: ActivationStepState =
      scansTotal === 0 ? 'todo' : scansThisWeek > 0 ? 'done' : 'in_progress';

    const isLoading = isLoadingOrder || (!!agentId && (isLoadingTotal || isLoadingWeek));

    return {
      printed,
      printedAt: order?.sent_at ?? null,
      printedVendorEmail: order?.vendor_email ?? null,
      placed,
      placedCount: checkedCount,
      placedTotal,
      scanned,
      scansThisWeek,
      scansTotal,
      isLoading,
      // Only claim this once the scan counts have actually loaded, otherwise a
      // slow request would read as "printed but dead".
      printedButNeverScanned:
        printed === 'done' && !isLoading && !!agentId && scansTotal === 0,
    };
  }, [
    order,
    checkedCount,
    totalScans,
    weekScans,
    isLoadingOrder,
    isLoadingTotal,
    isLoadingWeek,
    agentId,
  ]);
};
